import { useContext, useEffect, useState } from 'react'

import { Wrapper } from 'components'
import { TrainingItem } from 'api/types/trainings'
import { trainingService } from 'api/services/trainings'
import { format } from 'date-fns'
import {
  Badge,
  Button,
  Card,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from 'keep-react'
import { Link } from 'react-router-dom'
import { AuthContext } from 'contexts/AuthContext'
import { downloadPDF } from 'utils/downloadPdf'
import { AppContext } from 'contexts/AppContext'

export const PastTrainings = () => {
  //first load and is api being called
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isRequestingCertificate, setIsRequestingCertificate] =
    useState<boolean>(false)
  const { currentUser, currentBroker } = useContext(AuthContext)
  const { setIsCallingApi } = useContext(AppContext)

  // trainings data
  const [data, setData] = useState<TrainingItem[] | null>(null)

  // fetch training list from API
  const getTrainingList = async () => {
    try {
      const response = (await trainingService.getTrainingList({
        trainingStatus: '0',
      })) as TrainingItem[]
      setData(response || [])
      setIsLoading(false)
    } catch (error) {
      toast.error('Não foi possível carregar as formações.')
    }
  }

  const generateCertificate = async (conferenceId: string, name: string) => {
    const userEmail = currentUser?.Email

    if (!conferenceId || !userEmail) {
      return
    }
    setIsCallingApi(true)
    try {
      setIsRequestingCertificate(true)
      const response = await trainingService.generateCertificate({
        conferenceId,
        email: userEmail,
      })
      downloadPDF({
        base64Data: response,
        filename: `Certificado_${name}.pdf`,
      })
    } catch (error) {
      toast.error(
        'Não foi possível gerar o certificado. Aguarde algum tempo e tente novamente.',
      )
    } finally {
      setIsRequestingCertificate(false)
      setIsCallingApi(false)
    }
  }

  // initial data fetch
  useEffect(() => {
    getTrainingList()
  }, [])

  return (
    <div className="w-full flex-1 py-12 overflow-auto">
      <Wrapper>
        <h1
          className={`pb-10 text-xxxl font-medium ${currentBroker?.IsPrimePartner ? 'text-prime' : 'text-primary-600'} flex items-center justify-between`}
        >
          <span>Formações passadas</span>
        </h1>

        {isLoading || !data ? (
          <Card className="p-4 w-full max-w-full">
            <Spinner color="primary" className="mx-auto" />
          </Card>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Data/Hora</TableHead>
                  <TableHead>Formador</TableHead>
                  <TableHead>Vídeo</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data &&
                  data.map(
                    (training: TrainingItem, index: number) =>
                      !training.tutorial && (
                        <TableRow
                          key={`${training.id}-${index}`}
                          className="even:bg-primary-50/25"
                        >
                          <TableCell className="normal-case">
                            {training.name}
                          </TableCell>

                          <TableCell className="normal-case whitespace-nowrap">
                            {format(
                              new Date(training.date ?? new Date()),
                              'dd/MM/yyyy HH:mm',
                            )}
                          </TableCell>

                          <TableCell className="normal-case">
                            {training.teacher}
                          </TableCell>

                          <TableCell className="normal-case">
                            {training.conferenceVideo && (
                              <Button
                                color="primary"
                                variant="softBg"
                                size="xs"
                              >
                                <Link
                                  to={`https://www.youtube.com/watch?v=${training.conferenceVideo}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Ver vídeo
                                </Link>
                              </Button>
                            )}
                          </TableCell>

                          <TableCell>
                            {training.isUserRegistered ? (
                              <Button
                                color="primary"
                                variant="softBg"
                                size="xs"
                                className="ml-2"
                                disabled={isRequestingCertificate}
                                onClick={() =>
                                  generateCertificate(
                                    training.conferenceId!,
                                    training.name!,
                                  )
                                }
                              >
                                {isRequestingCertificate
                                  ? 'A gerar...'
                                  : 'Gerar Certificado'}
                              </Button>
                            ) : (
                              <Badge
                                color="secondary"
                                variant="base"
                                className="px-4 py-2 text-body-4 pointer-events-none whitespace-nowrap"
                              >
                                Não Participou
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ),
                  )}
              </TableBody>
            </Table>
          </>
        )}
      </Wrapper>
    </div>
  )
}
